/**
 * @file offline/useOffline.js
 * @description React hook for offline-first data access.
 * Components use this hook instead of authFetch for data that needs
 * to work offline. It handles the local-first + background sync pattern.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import offlineStore from "./offlineStore";
import { onSyncStateChange, syncAll, pullFromServer, downloadForOffline } from "./syncEngine";
import { useAuth } from "../contexts/AuthContext";

/**
 * Hook: useOfflineSync
 * Provides sync state to any component.
 * Returns: { isOnline, isSyncing, pendingCount, syncProgress, error, lastSyncTime,
 *            authRequired, sessionExpiresAt }   (the last two: Wave 2 §2.9)
 */
export function useOfflineSync() {
  const [state, setState] = useState({
    isOnline: navigator.onLine,
    isSyncing: false,
    pendingCount: 0,
    syncProgress: { current: 0, total: 0 },
    error: null,
    lastSyncTime: null,
    authRequired: false,
    sessionExpiresAt: null,
  });

  useEffect(() => {
    return onSyncStateChange(setState);
  }, []);

  return {
    ...state,
    triggerSync: syncAll,
  };
}

/**
 * Hook: useOfflineInspection
 * Load an inspection from IndexedDB first (instant), then update from server.
 * All saves go to IndexedDB first, then sync in background.
 */
export function useOfflineInspection(inspectionId) {
  const { authFetch } = useAuth();
  const [inspection, setInspection] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const responsesRef = useRef(responses);
  responsesRef.current = responses;

  // Load from IndexedDB first, then server
  const loadInspection = useCallback(async () => {
    try {
      // Step 1: Try IndexedDB (instant)
      const cached = await offlineStore.getInspection(inspectionId);
      const cachedResponses = await offlineStore.getResponses(inspectionId);

      if (cached) {
        setInspection(cached);
        if (cachedResponses.length > 0) {
          setResponses(cachedResponses);
        }
        setLoading(false);
      }

      // Step 2: Try server (background update)
      if (navigator.onLine) {
        try {
          const res = await authFetch("/api/inspections/" + inspectionId);
          if (res.ok) {
            const data = await res.json();
            const serverInspection = data.data?.inspection || data.inspection;
            const serverResponses = data.data?.responses || data.responses || [];

            // Cache to IndexedDB
            await offlineStore.cacheFullInspection(serverInspection, serverResponses, []);

            // Only update state if we don't have pending local changes
            const localPending = cachedResponses.some(r => r._syncStatus === "pending");
            if (!localPending) {
              setInspection(serverInspection);
              setResponses(serverResponses);
            } else {
              // Update inspection metadata but keep local responses
              setInspection(serverInspection);
            }
          }
        } catch (err) {
          // Server unavailable, stick with cached data
          console.warn("Server fetch failed, using cached data:", err.message);
        }
      }
    } catch (err) {
      console.error("Failed to load inspection:", err);
    } finally {
      setLoading(false);
    }
  }, [inspectionId, authFetch]);

  useEffect(() => {
    loadInspection();
  }, [loadInspection]);

  // Update a single response locally
  const updateResponse = useCallback((responseId, field, value) => {
    setResponses((prev) =>
      prev.map((r) => (r.id === responseId ? { ...r, [field]: value } : r))
    );
  }, []);

  // Save all responses — IndexedDB first, then server
  const saveResponses = useCallback(async () => {
    setSaving(true);
    try {
      const currentResponses = responsesRef.current;

      // Step 1: Save to IndexedDB (instant, never fails)
      await offlineStore.saveResponses(inspectionId, currentResponses);
      setLastSaved(new Date());

      // Step 2: Try to sync to server (background)
      if (navigator.onLine) {
        syncAll();
      }
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  }, [inspectionId]);

  return {
    inspection,
    responses,
    loading,
    saving,
    lastSaved,
    updateResponse,
    saveResponses,
    setInspection,
    setResponses,
    reload: loadInspection,
  };
}

/**
 * Hook: useOfflineDashboard
 * Load dashboard from cache, update from server in background.
 */
export function useOfflineDashboard() {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      // Step 1: Try cache
      const cached = await offlineStore.getCachedDashboard();
      if (cached && cached.data) {
        setData(cached.data);
        setLoading(false);
      }

      // Step 2: Try server
      if (navigator.onLine) {
        try {
          const res = await authFetch("/api/dashboard");
          if (res.ok) {
            const result = await res.json();
            const dashData = result.data || result;
            setData(dashData);
            await offlineStore.cacheDashboard(dashData);
          }
        } catch (err) {
          console.warn("Dashboard fetch failed, using cache:", err.message);
        }
      }

      setLoading(false);
    }
    load();
  }, [authFetch]);

  return { data, loading };
}

/**
 * Hook: useOfflineTemplates
 * Load templates from cache, update from server in background.
 */
export function useOfflineTemplates() {
  const { authFetch } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      // Step 1: Try cache
      const cached = await offlineStore.getTemplates();
      if (cached.length > 0) {
        setTemplates(cached);
        setLoading(false);
      }

      // Step 2: Try server
      if (navigator.onLine) {
        try {
          const res = await authFetch("/api/templates");
          if (res.ok) {
            const data = await res.json();
            const serverTemplates = data.data?.templates || data.templates || [];
            setTemplates(serverTemplates);

            // Cache each template with full details
            for (const tmpl of serverTemplates) {
              try {
                const fullRes = await authFetch("/api/templates/" + tmpl.id);
                if (fullRes.ok) {
                  const fullData = await fullRes.json();
                  const template = fullData.data?.template || fullData.template;
                  const sections = fullData.data?.sections || fullData.sections || [];
                  if (template) {
                    await offlineStore.cacheTemplate(template, sections);
                  }
                }
              } catch (e) {
                // Skip individual template fetch failures
              }
            }
          }
        } catch (err) {
          console.warn("Templates fetch failed, using cache:", err.message);
        }
      }

      setLoading(false);
    }
    load();
  }, [authFetch]);

  return { templates, loading };
}

/**
 * Hook: useOfflineInspections
 * Load inspections list from cache, update from server in background.
 */
export function useOfflineInspections() {
  const { authFetch } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cachedIds, setCachedIds] = useState(new Set());

  const checkCached = useCallback(async (inspList) => {
    const cached = new Set();
    for (const insp of inspList) {
      const isCached = await offlineStore.isInspectionCached(insp.id);
      if (isCached) cached.add(insp.id);
    }
    setCachedIds(cached);
  }, []);

  const load = useCallback(async () => {
    // Step 1: Try cache
    const cached = await offlineStore.getAllInspections();
    if (cached.length > 0) {
      setInspections(cached);
      setLoading(false);
      checkCached(cached);
    }

    // Step 2: Try server
    if (navigator.onLine) {
      try {
        const res = await authFetch("/api/inspections?limit=100");
        if (res.ok) {
          const data = await res.json();
          const serverInspections = data.data?.inspections || data.inspections || [];
          setInspections(serverInspections);

          // Update cache with server data (only non-pending)
          for (const insp of serverInspections) {
            const local = await offlineStore.getInspection(insp.id);
            if (!local || local._syncStatus === "synced") {
              await offlineStore.saveInspection({
                ...insp,
                _syncStatus: "synced",
                _serverId: insp.id,
              });
            }
          }

          checkCached(serverInspections);
        }
      } catch (err) {
        console.warn("Inspections fetch failed, using cache:", err.message);
      }
    }

    setLoading(false);
  }, [authFetch, checkCached]);

  useEffect(() => {
    load();
  }, [load]);

  // Download an inspection for offline
  const downloadInspection = useCallback(async (inspectionId) => {
    await downloadForOffline(inspectionId, authFetch);
    // Refresh cached status
    setCachedIds((prev) => new Set([...prev, inspectionId]));
  }, [authFetch]);

  return { inspections, loading, cachedIds, downloadInspection, reload: load };
}

export default {
  useOfflineSync,
  useOfflineInspection,
  useOfflineDashboard,
  useOfflineTemplates,
  useOfflineInspections,
};
